import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Users, TrendingUp, Mail, Phone, Plus, ArrowRight, Clock, FileText, ShieldCheck, Wand2 } from 'lucide-react';
import { HudCard, HudButton, HudBadge } from '../hud';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const opts = { withCredentials: true };

/**
 * ProjectCommercialSection — Espace commercial du projet.
 * Section 4 du nouveau menu projet.
 *  - Contacts CRM liés au projet (filtrés via project_id)
 *  - Opportunités liées + somme CA potentiel
 *  - Timeline simplifiée du cycle (CCTP → Analyse → Validation → Génération → Livraison)
 */
const ProjectCommercialSection = ({ projectId, project }) => {
  const [contacts, setContacts] = useState([]);
  const [opps, setOpps] = useState([]);
  const [pending, setPending] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) return;
    const load = async () => {
      try {
        const [c, o, p] = await Promise.all([
          axios.get(`${API}/crm/contacts?linked_project_id=${projectId}`, opts).catch(() => ({ data: { items: [] } })),
          axios.get(`${API}/crm/opportunities?project_id=${projectId}`, opts).catch(() => ({ data: { items: [] } })),
          axios.get(`${API}/materialtheque/projects/${projectId}/pending`, opts).catch(() => ({ data: { has_pending: false } })),
        ]);
        const contactsList = Array.isArray(c.data) ? c.data : (c.data?.items || []);
        const oppsList = Array.isArray(o.data) ? o.data : (o.data?.items || []);
        setContacts(contactsList);
        setOpps(oppsList);
        setPending(p.data);
      } catch {}
      setLoading(false);
    };
    load();
  }, [projectId]);

  const totalAmount = opps.reduce((s, op) => s + (op.amount || 0), 0);

  return (
    <div className="hud-stagger space-y-6">
      {/* Header KPI row */}
      <div className="grid grid-cols-3 gap-3">
        <Mini icon={Users}        label="Contacts"     value={contacts.length} />
        <Mini icon={TrendingUp}   label="Opportunités" value={opps.length} />
        <Mini icon={ShieldCheck}  label="CA pipeline"  value={fmtAmount(totalAmount)} tone="gold" />
      </div>

      {/* Contacts liés */}
      <HudCard variant="glass">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <span className="hud-eyebrow hud-eyebrow-gold">Contacts liés</span>
            <p className="mt-1 font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 17 }}>
              Acteurs sur ce projet
            </p>
          </div>
          <Link to={`/crm?project=${projectId}`}>
            <HudButton variant="secondary" size="sm" rightIcon={<ArrowRight className="w-3.5 h-3.5" />} data-testid="commercial-crm-link">
              Ouvrir CRM
            </HudButton>
          </Link>
        </div>
        {loading ? (
          <p className="text-[13px]" style={{ color: 'var(--c-text-muted)' }}>Chargement...</p>
        ) : contacts.length === 0 ? (
          <div className="rounded-xl border border-dashed flex flex-col items-center justify-center py-10 px-4 text-center"
               style={{ borderColor: 'rgba(255,255,255,0.10)' }}>
            <Users className="w-6 h-6 mb-2" style={{ color: 'var(--c-text-faint)' }} />
            <p className="text-[13px]" style={{ color: 'var(--c-text-2)' }}>
              Aucun contact lié à ce projet pour l'instant.
            </p>
            <p className="text-[11.5px] mt-0.5" style={{ color: 'var(--c-text-faint)' }}>
              Liez des contacts via le Mini-CRM en sélectionnant ce projet.
            </p>
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: 'var(--c-border)' }}>
            {contacts.slice(0, 6).map((c) => (
              <div key={c.id || c.contact_id} className="py-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-bold"
                     style={{ background: 'var(--c-gold-soft)', color: 'var(--c-gold)', border: '1px solid var(--c-border-accent)' }}>
                  {(c.first_name?.[0] || '?')}{(c.last_name?.[0] || '')}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium truncate" style={{ color: 'var(--c-text)' }}>
                    {c.first_name} {c.last_name}
                  </p>
                  <div className="flex items-center gap-2 text-[11px]" style={{ color: 'var(--c-text-muted)' }}>
                    {c.email && <span className="truncate"><Mail className="w-2.5 h-2.5 inline mr-1" />{c.email}</span>}
                    {c.phone && <span className="truncate"><Phone className="w-2.5 h-2.5 inline mr-1" />{c.phone}</span>}
                  </div>
                </div>
                {c.stage && <HudBadge tone="neutral">{c.stage}</HudBadge>}
              </div>
            ))}
          </div>
        )}
      </HudCard>

      {/* Timeline */}
      <HudCard variant="glass">
        <div className="flex items-center justify-between mb-5">
          <div>
            <span className="hud-eyebrow hud-eyebrow-gold">Cycle de production</span>
            <p className="mt-1 font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 17 }}>
              Timeline projet
            </p>
          </div>
          <HudBadge tone="gold"><Clock className="w-3 h-3" />Live</HudBadge>
        </div>
        <Timeline project={project} pending={pending} />
      </HudCard>
    </div>
  );
};

const Mini = ({ icon: Icon, label, value, tone = 'neutral' }) => (
  <div className="hud-card hud-card-glass" style={{ padding: 14 }}>
    <div className="flex items-start justify-between mb-1.5">
      <Icon className="w-4 h-4" style={{ color: tone === 'gold' ? 'var(--c-gold)' : 'var(--c-text-muted)' }} strokeWidth={1.75} />
      <span className="hud-eyebrow text-right">{label}</span>
    </div>
    <p className="hud-tabular font-semibold leading-none"
       style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 22 }}>
      {value}
    </p>
  </div>
);

const Timeline = ({ project, pending }) => {
  const has_cctp = !!(pending?.has_pending || project?.materialtheque_job_id);
  const cctp_integrated = !!project?.materialtheque_job_id;
  const stages = [
    { icon: FileText,    label: 'CCTP reçu',           desc: pending?.draft?.source_file_name || 'Document client transmis', done: has_cctp },
    { icon: Wand2,       label: 'Analyse IA',          desc: 'Extraction Nano Banana / Gemini Vision', done: has_cctp },
    { icon: ShieldCheck, label: 'Validation matériauthèque', desc: cctp_integrated ? 'Intégrée au prompt' : 'À valider par l\'équipe', done: cctp_integrated, active: pending?.has_pending },
    { icon: Wand2,       label: 'Génération maquettes', desc: 'Studio de rendu — Nano Banana 2 Pro',    done: false, active: cctp_integrated },
    { icon: Users,       label: 'Validation client',    desc: 'Plan de Vente partagé au promoteur',     done: false },
    { icon: ShieldCheck, label: 'Livraison',            desc: 'Pack finalisé · Photos + plans',         done: false },
  ];

  return (
    <ol className="space-y-1">
      {stages.map((s, i) => {
        const Icon = s.icon;
        const colorBg = s.done ? 'var(--c-gold)' : s.active ? 'var(--c-gold-soft)' : 'var(--c-surface-2)';
        const colorIc = s.done ? '#000' : s.active ? 'var(--c-gold)' : 'var(--c-text-muted)';
        return (
          <li key={i} className="flex items-start gap-3 py-2.5 relative">
            <div className="relative shrink-0">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center transition-colors"
                style={{
                  background: colorBg,
                  border: `1px solid ${s.done ? 'var(--c-gold)' : s.active ? 'var(--c-border-accent)' : 'var(--c-border)'}`,
                  boxShadow: s.done || s.active ? '0 0 12px rgba(217,160,91,0.30)' : 'none',
                }}
              >
                <Icon className="w-4 h-4" style={{ color: colorIc }} strokeWidth={2} />
              </div>
              {i !== stages.length - 1 && (
                <span
                  aria-hidden
                  className="absolute left-1/2 -translate-x-1/2 top-9 w-px"
                  style={{
                    height: 32,
                    background: s.done ? 'var(--c-gold)' : 'var(--c-border)',
                    opacity: s.done ? 0.4 : 1,
                  }}
                />
              )}
            </div>
            <div className="flex-1 min-w-0 pt-0.5">
              <div className="flex items-center gap-2">
                <span className="text-[13.5px] font-semibold" style={{ color: 'var(--c-text)' }}>
                  {s.label}
                </span>
                {s.active && <HudBadge tone="gold">En cours</HudBadge>}
                {s.done && !s.active && <HudBadge tone="success">Terminé</HudBadge>}
              </div>
              <p className="text-[12px] mt-0.5" style={{ color: 'var(--c-text-muted)' }}>
                {s.desc}
              </p>
            </div>
          </li>
        );
      })}
    </ol>
  );
};

const fmtAmount = (n) => {
  if (!n) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} M€`;
  if (n >= 1000) return `${(n / 1000).toFixed(0)} k€`;
  return `${n} €`;
};

export default ProjectCommercialSection;
